import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_BODY = `<p>Quick update from FGM —</p>

<p style="margin:24px 0 6px;color:#fff;font-family:Bebas Neue,sans-serif;letter-spacing:0.2em;font-size:18px;text-transform:uppercase;">🔥 Section Title Goes Here</p>
<p style="color:#d4d4d8;margin:0 0 20px;">Your body copy goes here. <strong style="color:#fff;">Bold</strong> what matters. Use <a href="https://fgmapparel.com" style="color:#E63946;">colored links</a> sparingly.</p>

<p style="margin:24px 0 6px;color:#fff;font-family:Bebas Neue,sans-serif;letter-spacing:0.2em;font-size:18px;text-transform:uppercase;">📈 Another Section</p>
<p style="color:#d4d4d8;margin:0 0 20px;">More content here.</p>`;

const TEMPLATES = [
    {
        id: "blank",
        name: "Blank — Start from scratch",
        subject: "",
        headline: "",
        body: DEFAULT_BODY,
    },
    {
        id: "drop_announce",
        name: "🔥 New Drop Announcement",
        subject: "New FGM Drop · Pre-Order Now",
        headline: "Drop · Live",
        body: `<p>It's here.</p>

<p style="margin:24px 0 6px;color:#fff;font-family:Bebas Neue,sans-serif;letter-spacing:0.2em;font-size:18px;text-transform:uppercase;">🔥 What's New</p>
<p style="color:#d4d4d8;margin:0 0 20px;">Describe the new pieces — 2-3 highlights. Mention price. Mention what makes them different.</p>

<p style="margin:24px 0 6px;color:#fff;font-family:Bebas Neue,sans-serif;letter-spacing:0.2em;font-size:18px;text-transform:uppercase;">⏰ Pre-Order Window</p>
<p style="color:#d4d4d8;margin:0 0 20px;">Pre-orders open now. Ships [DATE]. Sizes are limited — lock yours in.</p>

<p style="margin:32px 0 8px;color:#fff;font-family:Bebas Neue,sans-serif;letter-spacing:0.25em;font-size:20px;text-transform:uppercase;">Focus. Grind. Motivate.</p>`,
    },
    {
        id: "ambassador_update",
        name: "⭐ Ambassador Update",
        subject: "Ambassador Update — [TOPIC]",
        headline: "Ambassador Update",
        body: `<p>Hey ambassadors —</p>

<p style="margin:24px 0 6px;color:#fff;font-family:Bebas Neue,sans-serif;letter-spacing:0.2em;font-size:18px;text-transform:uppercase;">📈 What's New</p>
<p style="color:#d4d4d8;margin:0 0 20px;">Detail the update. Anything portal-related, payout-related, or commission-related goes here.</p>

<p style="margin:24px 0 6px;color:#fff;font-family:Bebas Neue,sans-serif;letter-spacing:0.2em;font-size:18px;text-transform:uppercase;">💸 What This Means For You</p>
<p style="color:#d4d4d8;margin:0 0 20px;">Translate the change into ambassador-impact language — earnings, rank progress, share tools.</p>`,
    },
    {
        id: "weekly_check_in",
        name: "📅 Weekly Check-In",
        subject: "Top Performers This Week + What's Coming",
        headline: "This Week At FGM",
        body: `<p style="margin:24px 0 6px;color:#fff;font-family:Bebas Neue,sans-serif;letter-spacing:0.2em;font-size:18px;text-transform:uppercase;">🏆 Top 3 Ambassadors This Week</p>
<p style="color:#d4d4d8;margin:0 0 20px;">1. [Name] — $XX in commissions<br/>2. [Name] — $XX<br/>3. [Name] — $XX</p>

<p style="margin:24px 0 6px;color:#fff;font-family:Bebas Neue,sans-serif;letter-spacing:0.2em;font-size:18px;text-transform:uppercase;">📦 What's Coming</p>
<p style="color:#d4d4d8;margin:0 0 20px;">Sneak peek at what's launching next week. Get your share links queued up.</p>`,
    },
];

export default function BroadcastTab({ password }) {
    const [subject, setSubject] = useState("");
    const [headline, setHeadline] = useState("");
    const [body, setBody] = useState(DEFAULT_BODY);
    const [ctaText, setCtaText] = useState("Open My Portal →");
    const [ctaUrl, setCtaUrl] = useState("https://fgmapparel.com/portal");
    const [audience, setAudience] = useState("ambassadors");
    const [testEmail, setTestEmail] = useState("");
    const [counts, setCounts] = useState({ ambassadors: 0, newsletter: 0 });
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");
    const [confirmFire, setConfirmFire] = useState(false);

    // Fetch recipient counts on mount
    useEffect(() => {
        const hdr = { "X-Admin-Password": password };
        Promise.all([
            axios.get(`${API}/admin/ambassadors`, { headers: hdr }).catch(() => ({ data: [] })),
            axios.get(`${API}/admin/newsletter`, { headers: hdr }).catch(() => ({ data: [] })),
        ]).then(([amb, nl]) => {
            setCounts({
                ambassadors: (amb.data || []).filter((a) => a.status === "accepted").length,
                newsletter: (nl.data || []).length,
            });
        });
    }, [password]);

    const applyTemplate = (id) => {
        const t = TEMPLATES.find((x) => x.id === id);
        if (!t) return;
        setSubject(t.subject);
        setHeadline(t.headline);
        setBody(t.body);
    };

    const totalRecipients = () => {
        if (audience === "ambassadors") return counts.ambassadors;
        if (audience === "newsletter") return counts.newsletter;
        return counts.ambassadors + counts.newsletter;
    };

    const validate = () => {
        if (!subject.trim()) return "Subject is required";
        if (!headline.trim()) return "Headline is required";
        if (!body.trim() || body.length < 30) return "Body must be at least 30 characters";
        if (!ctaText.trim()) return "CTA button text is required";
        if (!ctaUrl.trim() || !/^https?:\/\//.test(ctaUrl)) return "CTA URL must start with http(s)://";
        return null;
    };

    const validationError = validate();
    const fireDisabled = busy || totalRecipients() === 0 || !!validationError;

    const fire = async ({ test } = {}) => {
        const err = validate();
        if (err) {
            setError(err);
            return;
        }
        if (test && !testEmail.trim()) {
            setError("Enter your test email first");
            return;
        }
        setError("");
        setBusy(true);
        setResult(null);
        try {
            const payload = {
                subject: subject.trim(),
                headline: headline.trim(),
                body_html: body,
                cta_text: ctaText.trim(),
                cta_url: ctaUrl.trim(),
                audience,
            };
            if (test) payload.test_send_to = testEmail.trim();
            const r = await axios.post(`${API}/admin/ambassadors/broadcast`, payload, {
                headers: { "X-Admin-Password": password },
            });
            setResult(r.data);
            setConfirmFire(false);
        } catch (e) {
            setError(e?.response?.data?.detail || e.message || "Send failed");
        } finally {
            setBusy(false);
        }
    };

    const inputCls =
        "w-full bg-fgm-dark border border-white/10 px-3 py-2 text-white text-sm focus:border-[#E63946] focus:outline-none font-mono";
    const labelCls = "block text-[10px] font-display tracking-[0.3em] uppercase text-zinc-400 mb-2";

    return (
        <div data-testid="broadcast-tab" className="space-y-8">
            {/* Header */}
            <div className="bg-fgm-surface border border-white/10 p-6">
                <div className="flex items-center gap-3 mb-3">
                    <div className="h-px w-12 bg-[#E63946]" />
                    <span className="font-display tracking-[0.3em] text-xs text-zinc-400 uppercase">
                        Broadcast Composer
                    </span>
                </div>
                <h3 className="font-display text-3xl text-white uppercase mb-1">Send a Broadcast</h3>
                <p className="text-sm text-zinc-400">
                    Compose, preview, test-send to yourself, then fire to your full list. Rate-limited at ~4.5 emails/sec
                    so even large blasts ship cleanly.
                </p>
            </div>

            {/* Template picker */}
            <div className="bg-fgm-surface border border-white/10 p-6">
                <label className={labelCls}>Start From a Template</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {TEMPLATES.map((t) => (
                        <button
                            key={t.id}
                            type="button"
                            data-testid={`broadcast-template-${t.id}`}
                            onClick={() => applyTemplate(t.id)}
                            className="text-left p-4 bg-fgm-dark border border-white/10 hover:border-[#E63946]/60 transition-colors"
                        >
                            <div className="font-display tracking-[0.15em] uppercase text-white text-sm">{t.name}</div>
                        </button>
                    ))}
                </div>
            </div>

            {/* Form */}
            <div className="bg-fgm-surface border border-white/10 p-6 space-y-5">
                <div>
                    <label className={labelCls}>Subject Line (what they see in their inbox)</label>
                    <input
                        type="text"
                        data-testid="broadcast-subject"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder="Your FGM Portal just leveled up"
                        className={inputCls}
                    />
                </div>
                <div>
                    <label className={labelCls}>Headline (large text inside the email)</label>
                    <input
                        type="text"
                        data-testid="broadcast-headline"
                        value={headline}
                        onChange={(e) => setHeadline(e.target.value)}
                        placeholder="Your Portal · Just Got Real"
                        className={inputCls}
                    />
                </div>
                <div>
                    <label className={labelCls}>
                        Body (HTML allowed — use the template snippets above as a guide)
                    </label>
                    <textarea
                        data-testid="broadcast-body"
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        rows={14}
                        className={`${inputCls} font-mono text-xs leading-relaxed`}
                    />
                    <p className="text-[10px] text-zinc-500 mt-1.5">
                        {body.length.toLocaleString()} chars · supports `&lt;p&gt;`, `&lt;strong&gt;`, `&lt;a&gt;`, inline styles
                    </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                        <label className={labelCls}>CTA Button Text</label>
                        <input
                            type="text"
                            data-testid="broadcast-cta-text"
                            value={ctaText}
                            onChange={(e) => setCtaText(e.target.value)}
                            className={inputCls}
                        />
                    </div>
                    <div>
                        <label className={labelCls}>CTA Button URL</label>
                        <input
                            type="url"
                            data-testid="broadcast-cta-url"
                            value={ctaUrl}
                            onChange={(e) => setCtaUrl(e.target.value)}
                            className={inputCls}
                        />
                    </div>
                </div>
                <div>
                    <label className={labelCls}>Audience</label>
                    <div className="flex flex-wrap gap-2">
                        {[
                            { id: "ambassadors", label: `Ambassadors · ${counts.ambassadors}` },
                            { id: "newsletter", label: `Newsletter · ${counts.newsletter}` },
                            { id: "both", label: `Both · ${counts.ambassadors + counts.newsletter}` },
                        ].map((opt) => (
                            <button
                                key={opt.id}
                                type="button"
                                data-testid={`broadcast-audience-${opt.id}`}
                                onClick={() => setAudience(opt.id)}
                                className={`px-4 py-2 font-display tracking-[0.2em] text-xs uppercase border ${
                                    audience === opt.id
                                        ? "border-[#E63946] text-white bg-[#E63946]/10"
                                        : "border-white/10 text-zinc-400 hover:text-white"
                                }`}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Test send */}
            <div className="bg-fgm-surface border border-white/10 p-6">
                <label className={labelCls}>Test Send (deliver to one email only — no audience filtering)</label>
                <div className="flex flex-col sm:flex-row gap-3">
                    <input
                        type="email"
                        data-testid="broadcast-test-email"
                        value={testEmail}
                        onChange={(e) => setTestEmail(e.target.value)}
                        placeholder="you@fgmapparel.com"
                        className={`${inputCls} flex-1`}
                    />
                    <button
                        type="button"
                        data-testid="broadcast-send-test"
                        onClick={() => fire({ test: true })}
                        disabled={busy}
                        className="px-6 py-3 bg-white text-black font-display tracking-[0.25em] text-xs uppercase hover:bg-zinc-200 disabled:opacity-50 transition-colors"
                    >
                        {busy ? "Sending…" : "Send Test →"}
                    </button>
                </div>
            </div>

            {/* Send to all */}
            <div className="bg-fgm-surface border border-[#E63946]/40 p-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
                    <div>
                        <div className="font-display text-white text-2xl uppercase tracking-tight">Send To All</div>
                        <p className="text-sm text-zinc-400 mt-1">
                            <span className="text-[#E63946] font-bold">{totalRecipients()}</span> recipient
                            {totalRecipients() === 1 ? "" : "s"} — fires immediately, cannot be undone.
                        </p>
                    </div>
                    {!confirmFire ? (
                        <button
                            type="button"
                            data-testid="broadcast-fire-confirm"
                            onClick={() => setConfirmFire(true)}
                            disabled={fireDisabled}
                            title={validationError || ""}
                            className="px-6 py-3 bg-[#E63946] text-white font-display tracking-[0.25em] text-xs uppercase hover:bg-white hover:text-black disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            Send To All →
                        </button>
                    ) : (
                        <div className="flex gap-2">
                            <button
                                type="button"
                                data-testid="broadcast-fire"
                                onClick={() => fire()}
                                disabled={busy}
                                className="px-6 py-3 bg-[#E63946] text-white font-display tracking-[0.25em] text-xs uppercase hover:bg-white hover:text-black disabled:opacity-50 transition-colors"
                            >
                                {busy ? "Sending…" : "Confirm · Fire Blast"}
                            </button>
                            <button
                                type="button"
                                onClick={() => setConfirmFire(false)}
                                disabled={busy}
                                className="px-4 py-3 border border-white/10 text-zinc-400 font-display tracking-[0.25em] text-xs uppercase hover:text-white"
                            >
                                Cancel
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Result + errors */}
            {error && (
                <div data-testid="broadcast-error" className="bg-[#E63946]/10 border border-[#E63946] p-4 text-sm text-[#fbb]">
                    {error}
                </div>
            )}
            {result && (
                <div data-testid="broadcast-result" className="bg-fgm-surface border border-white/10 p-6">
                    <div className="font-display text-white text-xl uppercase tracking-tight mb-3">
                        {result.test_mode ? "Test Send Result" : "Blast Result"}
                    </div>
                    <div className="grid grid-cols-3 gap-4 mb-4">
                        <Stat label="Total" value={result.total} />
                        <Stat label="Sent" value={result.sent} accent="green" />
                        <Stat label="Failed" value={result.failed} accent={result.failed ? "red" : "neutral"} />
                    </div>
                    {result.details?.length > 0 && (
                        <div className="border-t border-white/10 pt-4 max-h-64 overflow-y-auto">
                            <table className="w-full text-xs font-mono">
                                <thead className="text-zinc-500 text-left">
                                    <tr>
                                        <th className="py-1">Email</th>
                                        <th className="py-1">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {result.details.map((d, i) => (
                                        <tr key={i} className="border-t border-white/5">
                                            <td className="py-1.5 text-zinc-300">{d.email || d.id}</td>
                                            <td className={`py-1.5 ${d.status === "sent" ? "text-emerald-400" : "text-[#fbb]"}`}>
                                                {d.status}
                                                {d.error && <span className="text-zinc-500"> · {d.error}</span>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function Stat({ label, value, accent = "neutral" }) {
    const color =
        accent === "green" ? "text-emerald-400" : accent === "red" ? "text-[#E63946]" : "text-white";
    return (
        <div className="bg-fgm-dark border border-white/10 p-4 text-center">
            <div className="text-[10px] font-display tracking-[0.3em] uppercase text-zinc-500 mb-1">{label}</div>
            <div className={`font-display text-3xl ${color}`}>{value}</div>
        </div>
    );
}
